import { Monitor, Moon, Sun } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workspaceAppearance as workspaceAppearanceApi, type WorkspaceAppearance } from "@/lib/api";
import { applyAppearance, cacheAppearance, getCachedAppearance } from "@/lib/appearance";
import { useWorkspaceAppearance, WORKSPACE_APPEARANCE_QUERY_KEY } from "@/hooks/useWorkspaceAppearance";

const MODES = [
    { value: "system", label: "System", icon: Monitor },
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
] as const;

const ACCENTS = [
    { value: "indigo", label: "Indigo", color: "#4f46e5" },
    { value: "blue", label: "Blue", color: "#2563eb" },
    { value: "teal", label: "Teal", color: "#0f766e" },
    { value: "green", label: "Green", color: "#15803d" },
    { value: "orange", label: "Orange", color: "#c2410c" },
    { value: "rose", label: "Rose", color: "#be123c" },
    { value: "neutral", label: "Neutral", color: "#475569" },
] as const;

export default function AppearanceSettings() {
    const queryClient = useQueryClient();
    const query = useWorkspaceAppearance();
    const appearance = query.data?.appearance ?? getCachedAppearance(query.data?.organizationId);

    const update = useMutation({
        mutationFn: workspaceAppearanceApi.update,
        onMutate: async (patch: Partial<WorkspaceAppearance>) => {
            await queryClient.cancelQueries({ queryKey: WORKSPACE_APPEARANCE_QUERY_KEY });
            const previous = queryClient.getQueryData<typeof query.data>(WORKSPACE_APPEARANCE_QUERY_KEY);
            const next = { ...appearance, ...patch };
            applyAppearance(next);
            cacheAppearance(previous?.organizationId, next);
            if (previous) {
                queryClient.setQueryData(WORKSPACE_APPEARANCE_QUERY_KEY, {
                    ...previous,
                    appearance: next,
                });
            }
            return { previous, previousAppearance: appearance };
        },
        onError: (_error, _patch, context) => {
            if (!context) return;
            if (context.previous) queryClient.setQueryData(WORKSPACE_APPEARANCE_QUERY_KEY, context.previous);
            applyAppearance(context.previousAppearance);
            cacheAppearance(context.previous?.organizationId, context.previousAppearance);
        },
        onSuccess: result => {
            queryClient.setQueryData(WORKSPACE_APPEARANCE_QUERY_KEY, result);
            applyAppearance(result.appearance);
            cacheAppearance(result.organizationId, result.appearance);
        },
    });

    return <section className="settings-card appearance-settings" aria-labelledby="appearance-heading">
        <div className="appearance-heading">
            <div>
                <h3 id="appearance-heading">Appearance</h3>
                <p className="muted">Saved to your profile for this organization.</p>
            </div>
            {update.isPending && <span className="appearance-saving" role="status">Saving…</span>}
        </div>

        <fieldset className="appearance-fieldset" disabled={update.isPending}>
            <legend>Color mode</legend>
            <div className="appearance-mode-options">
                {MODES.map(option => {
                    const Icon = option.icon;
                    const selected = appearance.colorMode === option.value;
                    return <button
                        type="button"
                        key={option.value}
                        className="appearance-mode-option"
                        aria-pressed={selected}
                        onClick={() => update.mutate({ colorMode: option.value })}
                    >
                        <Icon aria-hidden="true" />
                        <span>{option.label}</span>
                    </button>;
                })}
            </div>
        </fieldset>

        <fieldset className="appearance-fieldset" disabled={update.isPending}>
            <legend>Accent color</legend>
            <div className="appearance-accent-options">
                {ACCENTS.map(option => {
                    const selected = appearance.accentTheme === option.value;
                    return <button
                        type="button"
                        key={option.value}
                        className="appearance-accent-option"
                        aria-pressed={selected}
                        onClick={() => update.mutate({ accentTheme: option.value })}
                    >
                        <span className="appearance-swatch" style={{ background: option.color }} aria-hidden="true" />
                        <span>{option.label}</span>
                    </button>;
                })}
            </div>
        </fieldset>

        {query.isError && <p className="error">Your saved appearance could not be loaded. The cached theme is still active.</p>}
        {update.isError && <p className="error">Your appearance could not be saved. The previous theme has been restored.</p>}
    </section>;
}
